import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  MenuItem,
  SelectChangeEvent,
  List,
  ListItem,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../../store';
import { getCustomerCareTeam } from '../../../../../../store/customers/actionCreators';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import NewModal from '../../../../../Shared/NewModal';
import CustomerCareSelect from '../../../../../Shared/CustomerCareSelect';
import { MedicationEvent } from '../types';
import { GetMedEvent } from '../../../../../../generated';
import { DateTime } from 'luxon';
import { updateSingleMedEvent } from '../../../../../../store/hubOwner/actionCreators';
import {
  Status,
  mapApiStatusToEnum,
  getStatusBackgroundColor,
  getStatusIcon,
  getStatusLabel,
} from '../utils/statusUtils';
import { ReactComponent as CapsuleIcon } from '../../../../../../images/icons/capsule_inherit.svg';
import {
  active,
  backgroundActive,
  dimGrey,
  inactive,
  lightBackground1,
  lightBlack,
  lightTeal,
  orange,
} from '../../../../../../mainTheme';
import moment from 'moment';

// Constants
const COLORS = {
  active: active,
  inactive: inactive,
  text: {
    primary: lightBlack,
    secondary: dimGrey,
    tertiary: '#9999A3',
  },
  background: {
    active: backgroundActive,
    inactive: '#F6F6F9',
  },
} as const;

interface EditScheduleModalProps {
  open: boolean;
  onClose: () => void;
  event: MedicationEvent | null;
  ownerId: number;
}

// Reusable Components
const StyledTypography: React.FC<{
  variant: 'title' | 'subtitle' | 'description';
  children: React.ReactNode;
  sx?: any;
}> = ({ variant, children, sx }) => {
  const styles = {
    title: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0.2px',
      color: COLORS.text.primary,
    },
    subtitle: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '22px',
      letterSpacing: '0.2px',
      color: COLORS.text.primary,
    },
    description: {
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '16px',
      color: COLORS.text.tertiary,
    },
  };

  return <Typography sx={{ ...styles[variant], ...sx }}>{children}</Typography>;
};

interface RadioOptionProps {
  value: string;
  title: string;
  description: string;
  isSelected: boolean;
  children?: React.ReactNode;
}

const RadioOption: React.FC<RadioOptionProps> = ({
  value,
  title,
  description,
  isSelected,
  children,
}) => (
  <Box
    sx={{
      border: '1px solid',
      borderRadius: '8px',
      borderColor: isSelected ? COLORS.active : COLORS.inactive,
      p: 2,
      mb: 2,
      backgroundColor: isSelected
        ? COLORS.background.active
        : COLORS.background.inactive,
    }}
  >
    <FormControlLabel
      value={value}
      sx={{
        margin: 0,
        width: '100%',
        alignItems: 'flex-start',
        flexDirection: 'row-reverse', // This moves the radio to the right
        justifyContent: 'space-between', // This ensures proper spacing
        '& .MuiFormControlLabel-label': { flex: 1 },
      }}
      control={
        <Radio
          sx={{
            '&.MuiRadio-root': {
              color: isSelected ? lightTeal : COLORS.text.tertiary,
            },
          }}
        />
      }
      labelPlacement="start" // Changed to start to work with row-reverse
      label={
        <Box>
          <StyledTypography variant="subtitle"> {title}</StyledTypography>
          <StyledTypography variant="description">
            {description}
          </StyledTypography>
        </Box>
      }
    />
    {isSelected && children && <Box sx={{ mt: 2, ml: 0 }}>{children}</Box>}
  </Box>
);

const EditScheduleModal: React.FC<EditScheduleModalProps> = ({
  open,
  onClose,
  event,
  ownerId,
}) => {
  if (!open || !event) return null;

  const dispatch = useDispatch();
  const { careTeam, isLoading } = useSelector(
    (state: RootState) => state.customers,
  );

  const { med_window_time_sec } = useSelector(
    (state: RootState) => state.hubOwner,
  );
  const medWindowHours = (med_window_time_sec ?? 14400) / 3600;

  const careTeamArray = useMemo(() => {
    return Array.isArray(careTeam)
      ? careTeam
      : careTeam && typeof careTeam === 'object'
        ? Object.values(careTeam)[0]
        : [];
  }, [careTeam]);

  const generateTimeSelections = (
    prescribed_datetime?: string,
    timezone?: string,
  ) => {
    if (!prescribed_datetime || !timezone) return [];
    const start = DateTime.fromISO(prescribed_datetime, {
      zone: timezone,
    }).minus({ hours: medWindowHours });
    const totalSlots = (medWindowHours * 2 * 60) / 15 + 1;
    const times = Array(totalSlots)
      .fill(0)
      .map((_, index) => {
        const slotTime = start.plus({ minutes: 15 * index });
        // Convert to Design specified format
        const jsDate = slotTime.toJSDate();
        const label = moment.tz(jsDate, timezone).format('h:mm A z'); // e.g., 8:00 AM CST
        return {
          label,
          value: slotTime.toISO(),
        };
      });
    return times;
  };

  const timeSlots = useMemo(() => {
    return generateTimeSelections(event?.prescribed_datetime, event?.timezone);
  }, [event?.prescribed_datetime, medWindowHours]);

  const getAssistTypeFromEvent = (eventType?: string) => {
    if (
      eventType === GetMedEvent.event_type.CAREGIVER_ASSIST ||
      eventType === 'MED_ASSIST'
    ) {
      return GetMedEvent.event_type.CAREGIVER_ASSIST;
    }
    return GetMedEvent.event_type.SMART_ASSIST;
  };
  const [formData, setFormData] = useState({
    medicationTime: '',
    assistType: GetMedEvent.event_type.SMART_ASSIST,
    caregiver: '',
  });

  // Update formData when event changes
  useEffect(() => {
    if (event) {
      let defaultTime = '';
      if (event.desired_datetime) {
        const match = timeSlots.find(
          (slot) =>
            DateTime.fromISO(slot.value ?? '').toISO() ===
            DateTime.fromISO(event.desired_datetime ?? '').toISO(),
        );
        defaultTime = match ? (match.value ?? '') : (timeSlots[0]?.value ?? '');
      } else {
        defaultTime = timeSlots[0]?.value || '';
      }
      const defaultCaregiver = event.caregiver_customer_id
        ? String(event.caregiver_customer_id)
        : '';
      setFormData({
        medicationTime: defaultTime,
        assistType: getAssistTypeFromEvent(event.type),
        caregiver: defaultCaregiver,
      });
    }
  }, [event, timeSlots]);

  useEffect(() => {
    if (
      open &&
      formData.assistType === GetMedEvent.event_type.CAREGIVER_ASSIST &&
      !careTeam
    ) {
      dispatch(getCustomerCareTeam(ownerId));
    }
  }, [open, formData.assistType, careTeam, dispatch, ownerId]);

  const handleChange = useCallback(
    (field: string) => (event: SelectChangeEvent<string>) => {
      let value = event.target.value;
      // Always store caregiver as string
      if (field === 'caregiver') {
        value = value === '' ? '' : String(value);
      }
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        assistType: event.target.value as GetMedEvent.event_type,
      }));
    },
    [],
  );

  const handleSave = useCallback(() => {
    const payload: any = {
      med_event_id: event.id,
      desired_admin_time: formData.medicationTime,
      event_type: formData.assistType,
    };
    if (
      formData.assistType === GetMedEvent.event_type.CAREGIVER_ASSIST &&
      formData.caregiver
    ) {
      payload.caregiver_customer_id = formData.caregiver;
    }
    dispatch(updateSingleMedEvent(ownerId, payload));
    onClose();
  }, [
    event?.id,
    formData.medicationTime,
    formData.assistType,
    formData.caregiver,
    dispatch,
    ownerId,
    onClose,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const timezoneNotice = useMemo(() => {
    // Get user's current timezone
    const userTimeZone = DateTime.local().zoneName;

    // Patient's timezone from event or fallback
    const patientTimeZone = event?.timezone || 'Etc/UTC';

    // Get readable label for patient timezone
    const now = new Date();
    const patientAbbr = moment.tz(now, patientTimeZone).format('z'); // e.g., CST or MST
    const patientLongOffset =
      DateTime.now().setZone(patientTimeZone).offsetNameLong;
    const patientTimeZoneLabel = `${patientAbbr} (${patientLongOffset})`;

    // Show the notice if the user's timezone is different from the patient's
    const showTimeZoneNotice = userTimeZone !== patientTimeZone;

    return showTimeZoneNotice ? (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
        <FontAwesomeIcon
          icon={faCircleInfo}
          style={{
            fontSize: 16,
            color: '#9999A3',
            marginRight: '8px',
            marginTop: '2px',
          }}
        />
        <Box>
          <StyledTypography variant="description">
            The times shown below are according to the patient's time zone.
          </StyledTypography>
          <StyledTypography variant="description" sx={{ mb: 3 }}>
            Patient's time zone:{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {patientTimeZoneLabel}
            </Box>
          </StyledTypography>
        </Box>
      </Box>
    ) : null;
  }, [formData.assistType, event?.timezone]);

  const renderContent = () => (
    <Box>
      {/* Info Text - Shows when timezones differ */}
      {timezoneNotice}

      {/* Medication Time */}
      <Box sx={{ mb: 3 }}>
        <StyledTypography variant="title">Medication time</StyledTypography>
        <Box sx={{ mt: 1 }}>
          <CustomerCareSelect
            label=""
            value={formData.medicationTime}
            onChange={handleChange('medicationTime')}
            fullWidth
          >
            {timeSlots?.map((slot, idx) => (
              <MenuItem key={idx} value={slot.value ?? ''}>
                {slot.label}
              </MenuItem>
            ))}
          </CustomerCareSelect>
        </Box>
      </Box>

      {/* Frequency - Only shown when CAREGIVER_ASSIST is selected */}
      {formData.assistType === GetMedEvent.event_type.CAREGIVER_ASSIST && (
        <Box sx={{ mb: 3 }}>
          <StyledTypography variant="title" sx={{ mb: 1 }}>
            Frequency
          </StyledTypography>
          <StyledTypography
            variant="subtitle"
            sx={{ color: COLORS.text.tertiary }}
          >
            Occurs based on prescription schedule.
          </StyledTypography>
        </Box>
      )}

      {/* Type of Assist */}
      <Box sx={{ mb: 3 }}>
        <StyledTypography variant="title">Type of assist</StyledTypography>
        <Box sx={{ mt: 1 }}>
          <RadioGroup value={formData.assistType} onChange={handleRadioChange}>
            <RadioOption
              value={GetMedEvent.event_type.CAREGIVER_ASSIST}
              title="Caregiver Assist"
              description="Requires a video call from a caregiver"
              isSelected={
                formData.assistType === GetMedEvent.event_type.CAREGIVER_ASSIST
              }
            >
              <StyledTypography
                variant="subtitle"
                sx={{ color: dimGrey, ml: '16px' }}
              >
                Select contact for assist
              </StyledTypography>
              <Box sx={{ mt: 1 }}>
                {!isLoading ? (
                  <CustomerCareSelect
                    label=""
                    value={formData.caregiver}
                    onChange={handleChange('caregiver')}
                    fullWidth
                    sx={{ backgroundColor: lightBackground1 }}
                  >
                    {[
                      <MenuItem value="" key="none">
                        Select Caregiver
                      </MenuItem>,
                      ...careTeamArray.map((caregiver: any, idx: number) => (
                        <MenuItem
                          key={caregiver.customer_id ?? idx}
                          value={String(caregiver.customer_id)}
                        >
                          {caregiver.first_name && caregiver.last_name
                            ? `${caregiver.first_name} ${caregiver.last_name}`
                            : caregiver.email || 'No caregivers found'}
                        </MenuItem>
                      )),
                    ]}
                  </CustomerCareSelect>
                ) : (
                  <Box display="flex" justifyContent="center">
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            </RadioOption>

            <RadioOption
              value={GetMedEvent.event_type.SMART_ASSIST}
              title="Smart Assist"
              description="The Hub will alert and assist the patient"
              isSelected={
                formData.assistType === GetMedEvent.event_type.SMART_ASSIST
              }
            />
          </RadioGroup>
        </Box>
      </Box>

      {/* Medication Section */}
      <Box sx={{ mb: 3 }}>
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            border: 'none',
            borderRadius: '0',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{ fontSize: '14px', color: COLORS.text.secondary }}
              />
            }
            sx={{
              padding: '0',
              minHeight: 'auto',
              '& .MuiAccordionSummary-content': {
                margin: 0,
                alignItems: 'center',
              },
              '&.Mui-expanded': {
                minHeight: 'auto',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StyledTypography variant="title">Medication</StyledTypography>
              <StyledTypography variant="description">
                ({event.medications?.length ?? 0})
              </StyledTypography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0' }}>
            {event.medications && (
              <List
                dense
                sx={{
                  p: 0,
                  width: '100%',
                  maxHeight: '200px',
                  overflow: 'auto',
                  marginTop: 1,
                }}
              >
                {event.medications.map((medication, idx) => (
                  <ListItem
                    key={`${medication.id}-${idx}`}
                    sx={{ px: 0, py: 1 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Chip
                            label={medication.name}
                            sx={{
                              backgroundColor: getStatusBackgroundColor(
                                medication.status,
                              ),
                              color: lightBlack,
                              fontWeight: 500,
                              fontSize: 12,
                              px: 1.5,
                              height: 30,
                            }}
                          />
                          {mapApiStatusToEnum(medication.status) ===
                            Status.MISSED && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {getStatusIcon(medication.status)}
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  lineHeight: '16px',
                                  color: orange,
                                  fontWeight: 500,
                                }}
                              >
                                {getStatusLabel(medication.status)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box display="flex" alignItems="center" sx={{ m: 1 }}>
                          <CapsuleIcon
                            width={16}
                            height={16}
                            style={{
                              marginRight: 8,
                              display: 'inline-block',
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: '14px',
                              lineHeight: '16px',
                              color: dimGrey,
                              fontWeight: 500,
                            }}
                            component="span"
                          >
                            {medication.dosage} QTY{' '}
                            <Box
                              component="span"
                              sx={{
                                color: lightBlack,
                                fontWeight: 700,
                              }}
                            >
                              {medication.quantity}
                            </Box>
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );

  return (
    <NewModal
      title={event.title}
      showCloseX
      onClose={onClose}
      maxWidth="xs"
      contentPaddingTop="24px"
      actions={{
        primaryAction: {
          text: 'Save',
          onClick: handleSave,
        },
        secondaryAction: {
          text: 'Cancel',
          onClick: handleCancel,
          varient: 'text',
        },
      }}
    >
      {renderContent()}
    </NewModal>
  );
};

export default EditScheduleModal;
